import config from './config';
import { enableScroll, disableScroll, showStaticDevice, setSpriteOnCanvas } from "./common";
import { setSectionNumber } from "./scenes/section-indicators";
import { showScene2Slices, hideScene2Slices } from "./scenes/scene-2";
import scene4 from "./scenes/scene-4";

const instagramAgent = navigator.userAgent.indexOf("Instagram") > -1;

const scene1 = config["scene-1-7"];
const scene2 = config["scene-2"];
const scene3 = config["scene-3"];

const durationBase = 1.2;

const deviceCanvasContainer = document.getElementById('device-canvas-container');
const deviceCanvas = document.getElementById(
  "device-canvas"
) as HTMLCanvasElement;
const deviceContext = deviceCanvas.getContext("2d");

const deviceScreenVideo = document.getElementById(
  "device-screen-video"
) as HTMLVideoElement;

type Direction = 'next' | 'prev';

interface Scene {
  active?: boolean;
  selector?: string;
  id: string;
  enter(direction: Direction, onComplete: Function): TimelineLite;
  leave(direction: Direction, onComplete: Function): TimelineLite;
}

function animateDevice(scene: typeof scene1 | typeof scene3, index: number, direction: 'next' | 'prev'){
  console.log('ANIMATE DEVICE INIT', direction, index);
  function onUpdate() {
    let deviceAnimationIndex = index;
    const offset = deviceAnimationIndex * scene.keyframeSpan - 1;
    let progress = this.progress();
    if (direction === 'prev'){
      progress = 1 - progress;
    }
    let targetFrame = Math.floor(progress * scene.keyframeSpan + offset);
    setSpriteOnCanvas({
      context: deviceContext,
      targetFrameIndex: targetFrame,
      spriteSettings: scene.sprites,
    });
  };
  return onUpdate;
}

function animateScene2Explosion(direction: Direction) {
  function onUpdate() {
    let progress = this.progress();
    if (direction === "prev") {
      progress = 1 - progress;
    }
    let frameNumber = Math.floor(progress * scene2.maxFrameIndex);
    setSpriteOnCanvas({
      context: deviceContext,
      targetFrameIndex: frameNumber,
      spriteSettings: scene2.sprites,
    });
  }
  return onUpdate;
}

let scene2Specs : Array<HTMLElement>;
function animateScene2Spec(index: number, direction: Direction): TimelineLite {
  const lastSpecIndex = scene2Specs.length - 1;
  const spec = scene2Specs[index] as HTMLElement;

  const specType = spec.dataset.specType;
  const slices = gsap.utils.toArray(
    `#scene-2-slices-container img[data-spec-type="${specType}"]`
  );
  const notSlices = gsap.utils.toArray(
    `#scene-2-slices-container img:not([data-spec-type="${specType}"])`
  );

  const tl = gsap.timeline();
  tl.addLabel("start");
  tl.to(
    notSlices,
    {
      duration: durationBase,
      autoAlpha: 0.18,
    },
    "start"
  );
  tl.addLabel('land', 'start+=.5');
  tl.to(
    slices,
    {
      duration: durationBase/2,
      autoAlpha: 1,
    },
    "land"
  );
  tl.to(
    spec,
    {
      duration: durationBase,
      y: 0,
      autoAlpha: 1,
    },
    "land"
  );

  return tl;
}

function createSpecAnimationScene(
  scene: typeof scene1 | typeof scene3,
  specNumber: number,
  animationIndex: number
): Scene {
  const id = `${scene.id}-spec-list-${specNumber}`;
  return {
    id: id,
    enter: function (direction, onComplete) {
      console.log("ENTER", id, direction);
      const tl = gsap.timeline({
        paused: true,
        onUpdate:
          direction === "next"
            ? animateDevice(scene, animationIndex, direction)
            : undefined,
        onComplete: (onComplete as any) as gsap.Callback,
      });
      tl.addLabel("start");
      tl.to(
        `#${id}`,
        {
          duration: durationBase,
          autoAlpha: 1,
          y: 0,
        },
        "start+=.5"
      );
      return tl;
    },
    leave: function (direction, onComplete) {
      console.log("LEAVE", id, direction);
      const tl = gsap.timeline({
        paused: true,
        onUpdate:
          direction === "prev"
            ? animateDevice(scene, animationIndex, direction)
            : undefined,
        onComplete: (onComplete as any) as gsap.Callback,
      });
      tl.addLabel("start");
      tl.to(
        `#${id}`,
        {
          duration: durationBase,
          autoAlpha: 0,
          y: 200 * (direction === "next" ? -1 : 1),
        },
        "start"
      );
      return tl;
    },
  };
}

function createScene2SpecAnimationScene(specIndex: number): Scene {
  const scene = scene2;
  const id = `${scene.id}-spec-list-${specIndex + 1}`;
  return {
    id: id,
    enter: function (direction, onComplete) {
      console.log("ENTER", id, direction);
      const tl = gsap.timeline({
        paused: true,
        onComplete: (onComplete as any) as gsap.Callback,
      });
      tl.addLabel("start");
      tl.add(animateScene2Spec(specIndex, direction), 'start');
      return tl;
    },
    leave: function (direction, onComplete) {
      console.log("LEAVE", id, direction);
      const tl = gsap.timeline({
        paused: true,
        onComplete: (onComplete as any) as gsap.Callback,
      });
      tl.addLabel("start");
      tl.to(
        `#${id}`,
        {
          duration: durationBase,
          autoAlpha: 0,
          y: 200 * (direction === "next" ? -1 : 1),
        },
        "start"
      );
      return tl;
    },
  };
}

const scenes: Array<Scene> = [
  {
    active: true,
    id: "scene-1-0",
    enter: function (direction, onComplete) {
      console.log("ENTER 0", direction);
      const tl = gsap.timeline({
        paused: true,
        onComplete: (onComplete as any) as gsap.Callback,
      });
      tl.addLabel("start");
      tl.to(
        deviceCanvasContainer,
        {
          duration: 1.5,
          yPercent: -10,
          left: "50%",
          height: "88vw",
          width: "88vw",
          rotation: 0,
          onComplete: () => {
            gsap.to("#device-screen-video-wrapper", {
              duration: 0.15,
              autoAlpha: 0,
              display: "none",
            });
          },
        },
        "start"
      );
      tl.to(".scene-intro-header-first", {
        duration: 1,
        autoAlpha: 1,
      });
      tl.to(
        "#scroll-prompt",
        {
          duration: 0.25,
          yPercent: 0,
          autoAlpha: 1,
        },
        "start"
      );
      return tl;
    },
    leave: function (direction, onComplete) {
      console.log("LEAVE 0", direction);
      const tl = gsap.timeline({
        paused: true,
        onComplete: (onComplete as any) as gsap.Callback,
      });
      tl.addLabel("start");
      tl.to(".scene-intro-header-first", {
        duration: 1,
        yPercent: -20,
        autoAlpha: 0,
      });
      tl.to(
        "#scroll-prompt",
        {
          duration: 0.25,
          yPercent: -20,
          autoAlpha: 0,
        },
        "start"
      );
      return tl;
    },
  },
  {
    id: "scene-1-7-1",
    enter: function (direction, onComplete) {
      console.log("ENTER 1", direction);
      const tl = gsap.timeline({
        paused: true,
        onComplete: (onComplete as any) as gsap.Callback,
      });
      tl.addLabel("start");
      tl.to(
        deviceCanvasContainer,
        {
          duration: 1.5,
          yPercent: -27,
          xPercent: -20,
          height: "120vh",
          width: "120vh",
          rotation: -45,
          ease: Sine.easeInOut,
        },
        "start"
      );
      tl.addLabel("land", direction === "next" ? "start" : "start+=1.35");
      tl.call(
        function () {
          try {
            console.log("PLAY VIDEO scene17");
            deviceScreenVideo.play();
          } catch (e) {
            console.error("error playing scene 1-7 video");
          }
        },
        [],
        "land"
      );
      tl.to(
        "#device-screen-video-wrapper",
        {
          duration: 0.15,
          autoAlpha: 1,
          display: "block",
          onStart: function () {
            console.log("SHOW VIDEO, tlCanvasRotateGrow");
          },
        },
        "land"
      );
      tl.to(
        `#scene-1-7-spec-list-1`,
        {
          duration: 1,
          autoAlpha: 1,
          y: 0,
        },
        direction === "next" ? "land+=.5" : "land-=1"
      );
      tl.to("#section-indicators", {
        autoAlpha: 1,
        duration: durationBase,
        onStart: () => {
          setSectionNumber(1);
        },
      }, 'land');
      return tl;
    },
    leave: function (direction, onComplete) {
      console.log("LEAVE 1", direction);
      const tl = gsap.timeline({
        paused: true,
        onComplete: (onComplete as any) as gsap.Callback,
      });
      tl.addLabel("start");
      tl.to(
        `#scene-1-7-spec-list-1`,
        {
          autoAlpha: 0,
          y: 200 * (direction === "next" ? -1 : 1),
          duration: 1,
        },
        "start"
      );
      tl.to(
        deviceCanvasContainer,
        {
          duration: 1.5,
          yPercent: 0,
          xPercent: -50,
          height: "88vw",
          width: "88vw",
          rotation: 0,
          onComplete: () => {
            gsap.to("#device-screen-video-wrapper", {
              duration: 0.15,
              autoAlpha: 0,
              display: "none",
            });
          },
        },
        "start"
      );
      tl.call(
        function () {
          try {
            console.log("PAUSE VIDEO scene17");
            deviceScreenVideo.pause();
          } catch (e) {
            console.error("error pausing scene 1-7 video");
          }
        },
        [],
        "start"
      );
      tl.to(
        "#device-screen-video-wrapper",
        {
          duration: 0.15,
          autoAlpha: 0,
          display: "none",
          onStart: function () {
            console.log("HIDE VIDEO, tl LEAVE 1");
          },
        },
        "start"
      );
      if (direction === 'prev'){
        tl.to("#section-indicators", {
          autoAlpha: 0,
          duration: durationBase,
        }, 'start');
      }
      return tl;
    },
  },
  createSpecAnimationScene(scene1, 2, 0),
  createSpecAnimationScene(scene1, 3, 1),
  createSpecAnimationScene(scene1, 4, 2),
  createSpecAnimationScene(scene1, 5, 3),
  {
    id: "transition-to-scene-2",
    enter: function (direction, onComplete) {
      console.log("ENTER", "transition-to-scene-2", direction);
      const tl = gsap.timeline({
        paused: true,
        onComplete: (onComplete as any) as gsap.Callback,
      });
      tl.addLabel("start");
      if (direction === "next") {
        const tlFinalScene1Keyframe = gsap.timeline({
          onUpdate:
            direction === "next"
              ? animateDevice(scene1, scene1.keyframeCount - 1, direction)
              : undefined,
        });
        tlFinalScene1Keyframe.to("#nothing", {
          duration: durationBase,
          autoAlpha: 0,
        });
        tl.add(tlFinalScene1Keyframe, "start");
        tl.to("#section-indicators", {
          autoAlpha: 0,
          duration: durationBase,
        }, 'start');
        tl.addLabel("background");
        tl.to(
          "#PageContainer",
          {
            duration: durationBase / 1.5,
            background: "#F1F1F1",
            onStart: showStaticDevice,
          },
          "background"
        );
        tl.addLabel("explode");
        const tlExplode = gsap.timeline({
          onUpdate:
            direction === "next"
              ? animateScene2Explosion(direction)
              : undefined,
          onComplete: showScene2Slices,
        });
        tlExplode.to("#nothing", {
          duration: durationBase * 2,
          autoAlpha: 0,
        });
        tl.add(tlExplode, "explode");
        tl.addLabel("land");
        tl.to(
          "#device-canvas",
          {
            duration: durationBase,
            autoAlpha: 0,
          },
          "land"
        );
        tl.add(animateScene2Spec(0, direction), "land");
        tl.to("#section-indicators", {
          autoAlpha: 1,
          duration: durationBase,
          onStart: () => {
            setSectionNumber(2);
          },
        }, 'land');
      } else {
        tl.add(animateScene2Spec(0, direction), "start");
      }

      return tl;
    },
    leave: function (direction, onComplete) {
      console.log("LEAVE", "transition-to-scene-2", direction);
      const tl = gsap.timeline({
        paused: true,
        onComplete: (onComplete as any) as gsap.Callback,
      });
      tl.addLabel("start");

      if (direction === "next") {
        tl.to(
          "#scene-2-spec-list-1",
          {
            duration: durationBase,
            y: -200,
            autoAlpha: 0,
          },
          "start"
        );
      } else {
        tl.to(
          "#scene-2-spec-list-1",
          {
            duration: durationBase,
            y: 200,
            autoAlpha: 0,
          },
          "start"
        );
        tl.to(
          "#section-indicators",
          {
            autoAlpha: 0,
            duration: durationBase,
          },
          "start"
        );
        tl.to(
          "#device-canvas",
          {
            duration: durationBase,
            autoAlpha: 1,
            onComplete: hideScene2Slices,
          },
          "start"
        );
        tl.addLabel("unexplode", "start+=.5");
        const tlUnexplode = gsap.timeline({
          onUpdate:
            direction === "prev"
              ? animateScene2Explosion(direction)
              : undefined,
        });
        tlUnexplode.to("#nothing", {
          duration: durationBase * 2,
          autoAlpha: 0,
        });
        tl.add(tlUnexplode, "unexplode");

        tl.addLabel("background");
        tl.to(
          "#PageContainer",
          {
            duration: durationBase / 2,
            background: "#FFFFFF",
            onStart: showStaticDevice,
          },
          "background"
        );
        tl.addLabel("land");
        const tlFinalScene1Keyframe = gsap.timeline({
          onUpdate:
            direction === "prev"
              ? animateDevice(scene1, scene1.keyframeCount - 1, direction)
              : undefined,
        });
        tlFinalScene1Keyframe.to("#nothing", {
          duration: durationBase,
          autoAlpha: 0,
        });
        tl.add(tlFinalScene1Keyframe, "land");
      }
      return tl;
    },
  },
];
for (let i = 1; i < 9; i++){
  scenes.push(createScene2SpecAnimationScene(i));
}
scenes.push({
  id: "scene-2-spec-list-10",
  enter: function (direction, onComplete) {
    console.log("ENTER", "scene-2-spec-list-10", direction);
    const tl = gsap.timeline({
      paused: true,
      onComplete: (onComplete as any) as gsap.Callback,
    });
    tl.addLabel("start");
    if (direction === 'next'){
      console.log("ENTER", "tlUnexplode", direction);
      tl.to(
        "#device-canvas",
        {
          duration: durationBase/4,
          autoAlpha: 1,
          onComplete: hideScene2Slices,
          ease: Sine.easeIn
        },
        "start");
      tl.addLabel("unexplode");
      const tlUnexplode = gsap.timeline({
        onUpdate: animateScene2Explosion("prev") // reverse the animation
      });
      tlUnexplode.to("#nothing", {
        duration: durationBase * 2,
        autoAlpha: 1,
        onStart: function(){
          console.log('STARTING tlUnexplode timeline');
        }
      });
      tl.add(tlUnexplode, "unexplode");
      tl.addLabel('land', '-=1');
    } else {
      tl.addLabel("land", 'start');
    }

    tl.to(
      "#scene-2-spec-list-10",
      {
        duration: durationBase,
        y: 0,
        autoAlpha: 1,
      },
      "land"
    );
    return tl;
  },
  leave: function (direction, onComplete) {
    console.log("LEAVE", "scene-2-spec-list-10", direction);
    const tl = gsap.timeline({
      paused: true,
      onComplete: (onComplete as any) as gsap.Callback,
    });
    if (direction === "prev") {
      console.log("ENTER", "tlExplode", direction);
      tl.addLabel("explode");
      const tlExplode = gsap.timeline({
        onUpdate: animateScene2Explosion("next"),
      });
      tlExplode.to("#nothing", {
        duration: durationBase * 2,
        autoAlpha: 1,
        onStart: function () {
          console.log("STARTING tlExplode timeline");
        },
      });
      tl.add(tlExplode, "start");
      tl.to(
        "#device-canvas",
        {
          duration: durationBase / 4,
          autoAlpha: 0,
          onStart: showScene2Slices,
        },
        "start"
      );
    } else {
    }
    tl.to(
      "#scene-2-spec-list-10",
      {
        duration: durationBase,
        y: 200 * (direction === "next" ? -1 : 1),
        autoAlpha: 0,
      },
      "start"
    );
    return tl;
  },
});


scenes.push({
  id: "scene-3-spec-list-1",
  enter: function (direction, onComplete) {
    console.log("ENTER scene-3-spec-list-1", direction);
    const tl = gsap.timeline({
      paused: true,
      onComplete: (onComplete as any) as gsap.Callback,
    });
    tl.addLabel("start");
    tl.to(
      "#device-canvas-container",
      {
        yPercent: -80,
        opacity: 0,
        duration: durationBase,
        onStart: showStaticDevice,
      },
      "start"
    );
    tl.to(
      "#PageContainer",
      {
        background: "#000",
        duration: 1,
      },
      "start"
    );
    tl.addLabel('land');
    tl.set(
      "#device-canvas-container",
      {
        yPercent: 50,
        opacity: 0,
      },
    );
    tl.to(
      "#device-canvas-container",
      {
        yPercent: 0,
        opacity: 1,
        duration: durationBase,
        ease: Sine.easeOut,
        onStart: function(){
          setSpriteOnCanvas({
            context: deviceContext,
            targetFrameIndex: 0,
            spriteSettings: scene3.sprites,
          });
        },
        onUpdate: direction === "next"
            ? animateDevice(scene3, 0, direction)
            : undefined,
      },
      "land"
    );
    tl.to(
      `#scene-3-spec-list-1`,
      {
        duration: durationBase,
        autoAlpha: 1,
        y: 0,
      },
      "land+=.5"
    );
    tl.to(
      "#section-indicators",
      {
        autoAlpha: 1,
        duration: durationBase,
        onStart: () => {
          setSectionNumber(3);
        },
      },
      "land"
    );
    return tl;
  },
  leave: function (direction, onComplete) {
    console.log("ENTER scene-3-spec-list-1", direction);
    const tl = gsap.timeline({
      paused: true,
      onComplete: (onComplete as any) as gsap.Callback,
    });
    tl.addLabel("start");
    tl.to(
      `#scene-3-spec-list-1`,
      {
        duration: durationBase,
        autoAlpha: 0,
        y: 200 * (direction === "next" ? -1 : 1),
      },
      "start"
    );
    if (direction === 'prev'){
      tl.to(
        "#device-canvas-container",
        {
          yPercent: 50,
          opacity: 0,
          duration: durationBase,
        },
        "start"
      );
      tl.addLabel('transition');
      tl.to(
        "#PageContainer",
        {
          background: "#FFF",
          duration: durationBase,
        },
        "transition"
      );
      tl.fromTo(
        "#device-canvas-container",
        {
          yPercent: -80,
          opacity: 0,
        },
        {
          yPercent: 0,
          opacity: 1,
          duration: durationBase,
          onStart: showStaticDevice,
        },
        "transition"
      );

    }
    return tl;
  }
});

scenes.push(createSpecAnimationScene(scene3, 2, 1));
scenes.push(createSpecAnimationScene(scene3, 3, 2));
scenes.push(createSpecAnimationScene(scene3, 4, 3));
scenes.push(createSpecAnimationScene(scene3, 5, 4));

// Final scene:
// - Animate #mobile-spec-container content up and away,
// - BG back to white
// - set #mobile-spec-container to display:none to prevent event capture
// - Scroll window to top of #scene-4
scenes.push({
  id: 'activate-scene-4',
  enter: function (direction, onComplete) {
    console.log("ENTER", "activate-scene-4", direction);
    enableScroll();
    ScrollTrigger.refresh();
    const tl = gsap.timeline({
      paused: true,
      onComplete: (onComplete as any) as gsap.Callback,
    });
    tl.addLabel("start");
    tl.to(
      "#device-canvas-container",
      {
        duration: durationBase,
        autoAlpha: 0,
        yPercent: -140,
        ease: Sine.easeInOut,
        onComplete: () => {
          gsap.set('#mobile-spec-container', {
            display: 'none'
          });
        },
      },
      "start"
    );
    tl.to("#section-indicators", {
      autoAlpha: 0,
      duration: durationBase,
    }, 'start');
    tl.addLabel("land");
    tl.to(
      "#PageContainer",
      {
        background: "#fff",
        duration: durationBase,
      },
      "land"
    );


    const scene4Element = document.getElementById('scene-4');
    const boundingRect = scene4Element.getBoundingClientRect();
    const scrollTargetY = window.pageYOffset + boundingRect.top - 75;
    tl.to(
      window,
      {
        duration: durationBase * 1.5,
        scrollTo: {
          x: 0,
          y: scrollTargetY,
        },
        ease: Sine.easeOut,
      },
      "land"
    );
    tl.to("#section-indicators", {
      autoAlpha: 1,
      duration: durationBase,
      onStart: () => {
        setSectionNumber(4);
      }
    }, 'land+=.5');
    return tl;
  },
  leave: function (direction, onComplete) {
    console.log("LEAVE", "activate-scene-4", direction);
    disableScroll();
    ScrollTrigger.refresh();

    const tl = gsap.timeline({
      paused: true,
      onComplete: (onComplete as any) as gsap.Callback,
    });
    tl.addLabel("start");
    tl.to(
      window,
      {
        duration: durationBase,
        scrollTo: {
          x: 0,
          y: 0,
        },
        ease: Sine.easeOut,
      },
      "start"
    );
    tl.to(
      "#PageContainer",
      {
        background: "#000",
        duration: durationBase,
      },
      "start"
    );

    tl.addLabel('land');

    tl.to(
      "#device-canvas-container",
      {
        duration: durationBase,
        autoAlpha: 1,
        yPercent: 0,
        ease: Sine.easeInOut,
      },
      "land"
    );
    tl.to(
      `#scene-3-spec-list-5`,
      {
        duration: durationBase,
        autoAlpha: 1,
        y: 0,
        onStart: () => {
          gsap.set("#mobile-spec-container", {
            display: "block",
          });
        },
      },
      "land"
    );
    tl.to(
      "#section-indicators",
      {
        autoAlpha: 1,
        duration: durationBase,
        onStart: () => {
          setSectionNumber(4);
        }
      },
      "start"
    );
    return tl;
  },
})

let isAdvancing = false;

function advanceTimeline(direction: 'next' | 'prev'){
  if (isAdvancing){
    return;
  }
  isAdvancing = true;
  const currentIndex = scenes.findIndex((scene) => {
    return scene.active === true;
  });
  let targetIndex: number;
  if (direction === 'next'){
    targetIndex = currentIndex + 1;
  } else {
    targetIndex = currentIndex - 1;
  }
  if (targetIndex < 0){
    targetIndex = 0;
  } else if (targetIndex > (scenes.length - 1)) {
    targetIndex = scenes.length - 1;
  }

  const currentScene = scenes[currentIndex];
  const targetScene = scenes[targetIndex];
  console.log('ADVANCE TIMELINE', 'DIRECTION', direction, 'INDEX', targetIndex, 'current', currentScene, 'target', targetScene);

  let onCompleteHandlerThreshold = 1;
  let onCompleteHandlerCount = 0;

  const onComplete = () => {
    onCompleteHandlerCount++;
    if (onCompleteHandlerCount >= onCompleteHandlerThreshold) {
      if (currentScene) {
        currentScene.active = false;
      }
      targetScene.active = true;
      isAdvancing = false;
      console.log("ONCOMPLETE SCENE", targetIndex, direction, "scenes");
    }
  }
  if (targetScene !== currentScene){
    currentScene.leave(direction, onComplete).play();
    onCompleteHandlerThreshold = 2;
  }
  targetScene.enter(direction, onComplete).play();
}

let scrollStartTime: number, scrollStartTop: number;
function handleTouchStart(e: TouchEvent){
  e.preventDefault();
  scrollStartTime = new Date().getTime();
  scrollStartTop = e.changedTouches[0].pageY;
}
function handleTouchMove(e: TouchEvent) {
  e.preventDefault();
}
function handleTouchEnd(e: TouchEvent) {
  e.preventDefault();
  const deltaTime = new Date().getTime() - scrollStartTime;
  const deltaScroll = scrollStartTop - e.changedTouches[0].pageY;
  const velocityPixelsPerMillisecond = deltaScroll / deltaTime;
  if (Math.abs(velocityPixelsPerMillisecond) > 0.02) {
    if (velocityPixelsPerMillisecond > 0) {
      console.log("SWIPED UP NEXT", e.target); // element that was swiped
      advanceTimeline('next');
    } else {
      console.log("SWIPED DOWN PREV", e.target); // element that was swiped
      advanceTimeline("prev");
    }
  }
}

export default function init(callback: Function){
  const specs = document.querySelectorAll('.spec');
  const mobileSpecContainer = document.getElementById('mobile-spec-container');
  mobileSpecContainer.append(...specs);
  scene2Specs = gsap.utils.toArray(
    "#mobile-spec-container .spec[data-section-number='2']"
  );

  gsap.set('.site-header', {
    zIndex: 1000
  });
  gsap.set(mobileSpecContainer, {
    display: "block",
    visibility: "visible",
  });
  gsap.set(specs, {
    autoAlpha: 0,
    y: 200,
  });
  gsap.set("#device-screen-video-wrapper", {
    autoAlpha: 0,
  });

  mobileSpecContainer.addEventListener("touchstart", handleTouchStart);
  mobileSpecContainer.addEventListener("touchmove", handleTouchMove);
  mobileSpecContainer.addEventListener("touchend", handleTouchEnd);

  // mobileSpecContainer.addEventListener("gesturestart", (e) => {
  //   e.preventDefault();
  //   console.log('gesturestart', e);
  // });
  mobileSpecContainer.addEventListener("wheel", (e: WheelEvent) => {
    e.preventDefault();
    console.log("wheel", e);
    if (e.deltaY > 0){
      advanceTimeline("next");
    } else {
      advanceTimeline("prev");
    }
  });


  const landingWrapper = document.getElementById("landing-wrapper");
  function handleLandingWrapperScroll(e: TouchEvent) {
    e.preventDefault();
  }
  function preventBodyScroll(){
    landingWrapper.addEventListener(
      "touchstart",
      handleLandingWrapperScroll
    );
  }
  function restoreBodyScroll() {
    landingWrapper.removeEventListener(
      "touchstart",
      handleLandingWrapperScroll
    );
  }

  scene4();

  const tl4Prev = gsap.timeline({
    scrollTrigger: {
      id: `scene-4-scroll-back`,
      trigger: `#scene-4`,
      start: `top center`,
      scrub: false,
      onLeaveBack: () => {
        console.log('TRIGGER SCENES RESTORE');
        advanceTimeline('prev');
      },
      // markers: true,
    },
  });
  tl4Prev.to("#section-indicators", {
    duration: 0.25,
    autoAlpha: 0,
  });

  const tl4End = gsap.timeline({
    scrollTrigger: {
      id: `section-indicators-4-end`,
      trigger: `#scene-4-end`,
      start: `top bottom-=${5}`,
      scrub: false,
    },
  });
  tl4End.to(
    "#section-indicators",
    {
      duration: 0.25,
      autoAlpha: 0,
    }
  );

  console.log('mobile inited');
  // restoreBodyScroll();
  callback();
}